import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import api from "../api/axios";
import AdminShell from "./AdminShell";
import GpsManagementTabs from "./GpsManagementTabs";
import { NOT_AVAILABLE } from "./gpsManagementConstants";
import { loadGpsReplaySnapshot, MAX_REPLAY_RANGE_MS } from "../services/gpsReplaySnapshot";
import { loadGpsReplayGeometrySnapshot } from "../services/gpsReplayGeometrySnapshot";
import {
  createBackendReplayGeometryProvider, rawGpsReplayGeometryProvider, resolveReplayGeometry,
} from "../services/gpsReplayGeometryProvider";
import { summarizeRouteCoverage } from "../utils/gpsReplayGeometry";
import { frameAt, partitionSegmentsAt } from "../utils/gpsReplayEngine";
import "leaflet/dist/leaflet.css";
import "../styles/admin.css";

const PLAYBACK_RATES = Object.freeze([1, 2, 4]);
const NOOP = () => {};

function localInputValue(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function formatTime(value) {
  return value instanceof Date && Number.isFinite(value.getTime())
    ? value.toLocaleString()
    : NOT_AVAILABLE;
}

function formatDuration(durationMs) {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

function formatReceptionDelay(observation) {
  const delayMs = observation.receivedAt.getTime() - observation.recordedAt.getTime();
  return Number.isFinite(delayMs) ? formatDuration(Math.max(0, delayMs)) : NOT_AVAILABLE;
}

function titleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ReplayBounds({ geometry }) {
  const map = useMap();
  useEffect(() => {
    const points = geometry.observations.map((item) => [
      item.coordinates.latitude,
      item.coordinates.longitude,
    ]);
    if (points.length > 1) map.fitBounds(points, { padding: [32, 32], maxZoom: 16 });
    else if (points.length === 1) map.setView(points[0], 15);
  }, [geometry, map]);
  return null;
}

function replayMarkerIcon(heading) {
  const safeHeading = Number.isFinite(heading) ? heading : 0;
  return L.divIcon({
    className: "gps-replay-marker",
    html: `<span style="transform:rotate(${safeHeading}deg)">▲</span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function ReplayMap({ geometry, frame }) {
  const partition = useMemo(() => partitionSegmentsAt(geometry, frame), [frame, geometry]);
  const observationPath = useMemo(() => geometry.observations.map((observation) => [
    observation.coordinates.latitude,
    observation.coordinates.longitude,
  ]), [geometry]);
  const startCoordinates = geometry.matchedObservationCoordinates?.[0] ||
    geometry.observations[0]?.coordinates;
  const destinationCoordinates = geometry.matchedObservationCoordinates?.at(-1) ||
    geometry.observations.at(-1)?.coordinates;
  if (!frame) return <div className="gps-replay-empty">No trustworthy physical observations were found.</div>;
  return (
    <section className="gps-replay-map-card" aria-label="Route replay map">
      <MapContainer center={[12.8797, 121.774]} zoom={5} className="gps-replay-map">
        <ReplayBounds geometry={geometry} />
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {observationPath.length > 1 ? (
          <>
            <Polyline positions={observationPath}
              pathOptions={{ color: "#0f172a", weight: 11, opacity: 0.28,
                lineCap: "round", lineJoin: "round" }} />
            <Polyline positions={observationPath}
              pathOptions={{ color: "#2563eb", weight: 7, opacity: 0.96,
                lineCap: "round", lineJoin: "round" }} />
          </>
        ) : null}
        {partition.remaining.map((positions, index) => (
          <Polyline key={`remaining-${index}`} positions={positions}
            pathOptions={{ color: "#93c5fd", weight: 5, opacity: 0.95,
              dashArray: "10 9", lineCap: "round", lineJoin: "round" }} />
        ))}
        {partition.completed.map((positions, index) => (
          <Polyline key={`completed-${index}`} positions={positions}
            pathOptions={{ color: "#0ea5e9", weight: 7, opacity: 1,
              lineCap: "round", lineJoin: "round" }} />
        ))}
        {geometry.gaps.flatMap((gap, index) => [
          <CircleMarker key={`gap-from-${index}`}
            center={[gap.from.coordinates.latitude, gap.from.coordinates.longitude]}
            radius={6} pathOptions={{ color: "#d97706", fillColor: "#f59e0b", fillOpacity: 1 }}>
            <Tooltip>GPS gap begins</Tooltip>
          </CircleMarker>,
          <CircleMarker key={`gap-to-${index}`}
            center={[gap.to.coordinates.latitude, gap.to.coordinates.longitude]}
            radius={6} pathOptions={{ color: "#d97706", fillColor: "#fff", fillOpacity: 1 }}>
            <Tooltip>GPS gap ends</Tooltip>
          </CircleMarker>,
        ])}
        {geometry.dwells.map((dwell, index) => (
          <CircleMarker key={`dwell-${index}`}
            center={[dwell.from.coordinates.latitude, dwell.from.coordinates.longitude]}
            radius={5} pathOptions={{ color: "#0f766e", fillColor: "#ccfbf1", fillOpacity: 1 }}>
            <Tooltip>Stationary dwell interval</Tooltip>
          </CircleMarker>
        ))}
        {startCoordinates ? (
          <CircleMarker center={[startCoordinates.latitude, startCoordinates.longitude]}
            radius={9} pathOptions={{ color: "#ffffff", weight: 4,
              fillColor: "#16a34a", fillOpacity: 1 }}>
            <Tooltip permanent direction="top" offset={[0, -10]}>Start</Tooltip>
          </CircleMarker>
        ) : null}
        {destinationCoordinates ? (
          <CircleMarker center={[destinationCoordinates.latitude, destinationCoordinates.longitude]}
            radius={9} pathOptions={{ color: "#ffffff", weight: 4,
              fillColor: "#dc2626", fillOpacity: 1 }}>
            <Tooltip permanent direction="top" offset={[0, -10]}>Destination</Tooltip>
          </CircleMarker>
        ) : null}
        <Marker position={[frame.coordinates.latitude, frame.coordinates.longitude]}
          icon={replayMarkerIcon(frame.observation.headingDegrees)}>
          <Tooltip>{frame.inGap ? "Last known position — movement gap"
            : frame.inDwell ? "Stationary dwell position" : "Replay position"}</Tooltip>
        </Marker>
      </MapContainer>
      <div className="gps-replay-map-legend">
        <span><i className="is-traveled" /> Traveled observation path</span>
        <span><i className="is-remaining" /> Remaining observation path</span>
        <span><i className="is-start" /> Start</span>
        <span><i className="is-destination" /> Destination</span>
        <span><i className="is-dwell" /> Stationary dwell</span>
        <span><i className="is-gap" /> Movement gap endpoints</span>
      </div>
    </section>
  );
}

export default function GpsRouteReplay({
  activeView = "replay",
  onViewChange = NOOP,
  initialSelection = null,
  onInitialSelectionConsumed = NOOP,
  geometryProvider = rawGpsReplayGeometryProvider,
  requestedGeometryProvider = "raw_gps",
}) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(() => initialSelection?.vehicleId || "");
  const [from, setFrom] = useState(() => initialSelection?.from
    ? localInputValue(new Date(initialSelection.from))
    : localInputValue(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(() => initialSelection?.to
    ? localInputValue(new Date(initialSelection.to))
    : localInputValue(new Date()));
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [replayTimestampMs, setReplayTimestampMs] = useState(null);
  const [tripContext, setTripContext] = useState(() => initialSelection?.trip ? {
    ...initialSelection.trip,
    startAt: initialSelection.trip.startAt || initialSelection.from,
    endAt: initialSelection.trip.endAt || initialSelection.to,
  } : null);
  const [geometryFallbackReason, setGeometryFallbackReason] = useState(null);
  const fleetAbort = useRef(null);
  const historyAbort = useRef(null);
  const animationFrame = useRef(null);
  const lastAnimationAt = useRef(null);
  const consumedSelection = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    fleetAbort.current = controller;
    api.get("/gps/fleet/live", {
      params: { page: 1, limit: 1000, source: "physical" }, signal: controller.signal,
    }).then((response) => {
      const physical = (response.data?.items || []).filter((item) => item.source === "physical");
      setVehicles(physical);
      setVehicleId((current) => current || physical[0]?.vehicle?.id || "");
    }).catch((caught) => {
      if (caught?.code !== "ERR_CANCELED") setError("Physical vehicles could not be loaded for replay.");
    });
    return () => controller.abort();
  }, []);

  useEffect(() => () => {
    historyAbort.current?.abort();
    if (animationFrame.current) window.cancelAnimationFrame(animationFrame.current);
  }, []);

  const effectiveGeometryProvider = useMemo(() =>
    snapshot?.geometryContract?.status === "matched"
      ? createBackendReplayGeometryProvider(snapshot.geometryContract)
      : geometryProvider,
  [geometryProvider, snapshot]);
  const geometryResolution = useMemo(
    () => snapshot ? resolveReplayGeometry({ snapshot, provider: effectiveGeometryProvider }) : null,
    [effectiveGeometryProvider, snapshot]
  );
  const geometry = geometryResolution?.geometry || null;
  const coverage = useMemo(() => geometry ? summarizeRouteCoverage(geometry) : null, [geometry]);
  const startMs = geometry?.observations[0]?.recordedAt.getTime() ?? null;
  const endMs = geometry?.observations.at(-1)?.recordedAt.getTime() ?? null;
  const frame = useMemo(
    () => geometry && replayTimestampMs !== null ? frameAt(geometry, replayTimestampMs) : null,
    [geometry, replayTimestampMs]
  );

  useEffect(() => {
    if (!playing || startMs === null || endMs === null) return undefined;
    lastAnimationAt.current = null;
    const tick = (animationAt) => {
      if (lastAnimationAt.current === null) lastAnimationAt.current = animationAt;
      const elapsed = animationAt - lastAnimationAt.current;
      lastAnimationAt.current = animationAt;
      setReplayTimestampMs((current) => Math.min(endMs, (current ?? startMs) + elapsed * playbackRate));
      animationFrame.current = window.requestAnimationFrame(tick);
    };
    animationFrame.current = window.requestAnimationFrame(tick);
    return () => {
      if (animationFrame.current) window.cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
      lastAnimationAt.current = null;
    };
  }, [endMs, playbackRate, playing, startMs]);

  useEffect(() => {
    if (playing && endMs !== null && replayTimestampMs >= endMs) setPlaying(false);
  }, [endMs, playing, replayTimestampMs]);

  const loadReplayRange = useCallback(async (selection = null) => {
    const selectedVehicleId = selection?.vehicleId || vehicleId;
    const rangeFrom = new Date(selection?.from || from);
    const rangeTo = new Date(selection?.to || to);
    if (!selectedVehicleId || !Number.isFinite(rangeFrom.getTime()) || !Number.isFinite(rangeTo.getTime()) ||
        rangeFrom >= rangeTo || rangeTo - rangeFrom > MAX_REPLAY_RANGE_MS) {
      setError("Choose a physical vehicle and a valid range of no more than 7 days.");
      return;
    }
    historyAbort.current?.abort();
    const controller = new AbortController();
    historyAbort.current = controller;
    setPlaying(false);
    setLoading(true);
    setError("");
    setSnapshot(null);
    setGeometryFallbackReason(null);
    setReplayTimestampMs(null);
    try {
      let loaded;
      if (requestedGeometryProvider === "osrm") {
        try {
          loaded = await loadGpsReplayGeometrySnapshot({
            vehicleId: selectedVehicleId, from: rangeFrom, to: rangeTo,
            provider: "osrm", signal: controller.signal,
          });
          if (loaded.geometryContract.fallback.used) {
            setGeometryFallbackReason(loaded.geometryContract.fallback.reason || "road_matching_unavailable");
          }
        } catch (geometryError) {
          if (geometryError?.code === "ERR_CANCELED" || geometryError?.name === "CanceledError") throw geometryError;
          loaded = await loadGpsReplaySnapshot({
            vehicleId: selectedVehicleId, from: rangeFrom, to: rangeTo, signal: controller.signal,
          });
          setGeometryFallbackReason("road_matching_unavailable");
        }
      } else {
        loaded = await loadGpsReplaySnapshot({
          vehicleId: selectedVehicleId, from: rangeFrom, to: rangeTo, signal: controller.signal,
        });
      }
      setSnapshot(loaded);
      setReplayTimestampMs(loaded.observations[0]?.recordedAt.getTime() ?? null);
    } catch (caught) {
      if (caught?.code !== "ERR_CANCELED" && caught?.name !== "CanceledError") {
        setError("The complete replay snapshot could not be loaded. No partial route is shown.");
      }
    } finally {
      if (historyAbort.current === controller) historyAbort.current = null;
      setLoading(false);
    }
  }, [from, requestedGeometryProvider, to, vehicleId]);

  useEffect(() => {
    if (!initialSelection?.vehicleId || !initialSelection?.from || !initialSelection?.to) return;
    const key = `${initialSelection.vehicleId}|${initialSelection.from}|${initialSelection.to}|${initialSelection.trip?.id || ""}`;
    if (consumedSelection.current === key) return;
    consumedSelection.current = key;
    const rangeFrom = new Date(initialSelection.from);
    const rangeTo = new Date(initialSelection.to);
    if (Number.isFinite(rangeFrom.getTime()) && Number.isFinite(rangeTo.getTime())) {
      setTripContext(initialSelection.trip ? {
        ...initialSelection.trip,
        startAt: initialSelection.trip.startAt || initialSelection.from,
        endAt: initialSelection.trip.endAt || initialSelection.to,
      } : null);
      setVehicleId(initialSelection.vehicleId);
      setFrom(localInputValue(rangeFrom));
      setTo(localInputValue(rangeTo));
      loadReplayRange(initialSelection);
    }
    onInitialSelectionConsumed();
  }, [initialSelection, loadReplayRange, onInitialSelectionConsumed]);

  const restart = () => {
    setPlaying(false);
    if (startMs !== null) setReplayTimestampMs(startMs);
  };
  const togglePlayback = () => {
    if (startMs === null || endMs === null) return;
    if (!playing && replayTimestampMs >= endMs) setReplayTimestampMs(startMs);
    setPlaying((current) => !current);
  };
  const selectedVehicle = vehicles.find((item) => item.vehicle?.id === vehicleId);

  return (
    <AdminShell pageClassName="admin-shell--live-monitoring" eyebrow="FLEET HISTORY" title="Route Replay"
      subtitle="Truthful playback from ordered physical GPS observations.">
      <div className="gps-replay-dashboard">
        <GpsManagementTabs activeView={activeView} onViewChange={onViewChange} />
        {tripContext ? <section className="gps-replay-trip-context" aria-label="Trip replay context">
          <div><span>Trip Replay</span><strong>{formatTime(new Date(tripContext.startAt))} → {formatTime(new Date(tripContext.endAt))}</strong></div>
          <div><span>Observation coverage</span><strong>{titleCase(tripContext.coverage)}</strong></div>
        </section> : null}
        <section className="gps-replay-query" aria-label="Replay range">
          <label>Physical vehicle<select aria-label="Replay vehicle" value={vehicleId}
            onChange={(event) => { setVehicleId(event.target.value); setSnapshot(null); setPlaying(false); setGeometryFallbackReason(null); }}>
            <option value="">Select a vehicle</option>
            {vehicles.map((item) => <option key={item.vehicle.id} value={item.vehicle.id}>
              {item.vehicle.plateNumber} — {[item.vehicle.make, item.vehicle.model].filter(Boolean).join(" ")}
            </option>)}
          </select></label>
          <label>From<input aria-label="Replay from" type="datetime-local" value={from}
            onChange={(event) => setFrom(event.target.value)} /></label>
          <label>To<input aria-label="Replay to" type="datetime-local" value={to}
            onChange={(event) => setTo(event.target.value)} /></label>
          <button type="button" onClick={() => loadReplayRange()} disabled={loading || !vehicleId}>
            {loading ? "Loading complete snapshot..." : "Load route replay"}
          </button>
        </section>
        {error ? <div className="gps-replay-state gps-replay-state--error" role="alert">{error}</div> : null}
        {snapshot && snapshot.observations.length === 0 ? (
          <div className="gps-replay-state">No physical GPS observations were found for this range.</div>
        ) : null}
        {geometry && frame ? <>
          <section className="gps-replay-provider-status" aria-label="Route geometry status">
            <div><span>Route geometry</span><strong>{geometry.provider === "osrm" ? "Road matched" : "Raw GPS observations"}</strong></div>
            {geometry.provider === "osrm" && Number.isFinite(geometry.matchedRoadDistanceMeters) ?
              <div><span>Matched road distance</span><strong>{(geometry.matchedRoadDistanceMeters / 1000).toFixed(2)} km</strong></div> : null}
            {geometryFallbackReason || geometryResolution?.fallback ? <p role="status">
              Road matching unavailable — showing raw GPS observations.
            </p> : null}
          </section>
          <div className="gps-replay-heading">
            <div><span>Vehicle</span><strong>{selectedVehicle?.vehicle?.plateNumber || NOT_AVAILABLE}</strong></div>
            <div><span>Observations</span><strong>{geometry.observations.length}</strong></div>
            <div><span>Movement gaps</span><strong>{geometry.gaps.length}</strong></div>
            <div><span>Stationary dwell intervals</span><strong>{geometry.dwells.length}</strong></div>
          </div>
          <ReplayMap geometry={geometry} frame={frame} />
          <section className="gps-replay-console" aria-label="Replay controls">
            <div className="gps-replay-timeline-labels"><span>{formatTime(new Date(startMs))}</span><span>{formatTime(new Date(endMs))}</span></div>
            <input aria-label="Replay timeline" type="range" min={startMs} max={Math.max(startMs + 1, endMs)} step="1000"
              value={replayTimestampMs} onChange={(event) => { setPlaying(false); setReplayTimestampMs(Number(event.target.value)); }} />
            <div className="gps-replay-buttons">
              <button type="button" onClick={restart}>Restart</button>
              <button type="button" onClick={togglePlayback}>{playing ? "Pause" : "Play"}</button>
              <div role="group" aria-label="Playback speed">
                {PLAYBACK_RATES.map((rate) => <button key={rate} type="button"
                  className={playbackRate === rate ? "is-active" : ""}
                  aria-pressed={playbackRate === rate} onClick={() => setPlaybackRate(rate)}>{rate}x</button>)}
              </div>
            </div>
          </section>
          <section className="gps-replay-telemetry" aria-label="Current replay state">
            <h2>Current replay state</h2>
            <dl>
              <div><dt>Replay time</dt><dd>{formatTime(frame.timestamp)}</dd></div>
              <div><dt>Observation time</dt><dd>{formatTime(frame.observation.recordedAt)}</dd></div>
              <div><dt>Speed</dt><dd>{frame.observation.speedKph} km/h</dd></div>
              <div><dt>Heading</dt><dd>{frame.observation.headingDegrees}°</dd></div>
              <div><dt>Ignition</dt><dd>{frame.observation.ignition === null ? NOT_AVAILABLE : frame.observation.ignition ? "On" : "Off"}</dd></div>
              <div><dt>Movement</dt><dd>{titleCase(frame.movementState)}</dd></div>
              <div><dt>Observation metadata</dt><dd>{titleCase(frame.quality)}</dd></div>
              <div><dt>Reception delay</dt><dd>{formatReceptionDelay(frame.observation)}</dd></div>
              <div><dt>Event ordering</dt><dd>{frame.observation.outOfOrder ? "Out of order" : "In order"}</dd></div>
              <div><dt>Accuracy</dt><dd>{frame.observationMetadata.accuracyMeters === null ? "Not reported" : `${frame.observationMetadata.accuracyMeters} m`}</dd></div>
              <div><dt>Satellites</dt><dd>{frame.observationMetadata.satelliteCount ?? "Not reported"}</dd></div>
            </dl>
            {frame.inGap ? <div className="gps-replay-gap-active" role="status">
              Unknown movement interval — holding the last known position until the next real observation.
            </div> : null}
            {frame.inDwell ? <div className="gps-replay-dwell-active" role="status">
              Stationary dwell — replay time advances while the marker remains at the last stationary location.
            </div> : null}
            {frame.inInvalidEdge ? <div className="gps-replay-gap-active" role="status">
              Invalid or out-of-order interval — route continuity is not shown.
            </div> : null}
          </section>
          <section className="gps-replay-coverage" aria-label="Route observation coverage">
            <h2>Route observation coverage</h2>
            <p>This describes telemetry coverage only. It does not claim road or map-matching accuracy.</p>
            <dl>
              <div><dt>Timeline</dt><dd>{titleCase(coverage.classification)}</dd></div>
              <div><dt>Observation density</dt><dd>{coverage.observationDensityPerHour === null ? NOT_AVAILABLE : `${coverage.observationDensityPerHour}/hour`}</dd></div>
              <div><dt>Long moving intervals</dt><dd>{coverage.movingTimestampGapCount}</dd></div>
              <div><dt>Spatial uncertainty gaps</dt><dd>{coverage.spatialGapCount}</dd></div>
              <div><dt>Unknown travel duration</dt><dd>{formatDuration(coverage.unknownTravelDurationMs)}</dd></div>
              <div><dt>Invalid edges</dt><dd>{coverage.invalidEdgeCount}</dd></div>
            </dl>
          </section>
          {geometry.gaps.length ? <section className="gps-replay-gaps" aria-label="GPS gaps">
            <h2>Movement / GPS coverage gaps</h2>
            <p>Unknown intervals are not drawn or animated as confirmed travel.</p>
            {geometry.gaps.map((gap, index) => <div key={`${gap.from.recordedAt.toISOString()}-${index}`}>
              <strong>Gap {index + 1}: {formatDuration(gap.durationMs)}</strong>
              <span>{formatTime(gap.from.recordedAt)} → {formatTime(gap.to.recordedAt)}</span>
              <span>{titleCase(gap.reason)} · {(gap.distanceMeters / 1000).toFixed(2)} km chord</span>
            </div>)}
          </section> : null}
        </> : null}
      </div>
    </AdminShell>
  );
}
